import { z } from "zod";
import { SOURCE_TYPES } from "@/types/sourceTypes";
import { cnsToolExecutionArtifactSchema } from "@/lib/toolExecutionArtifact";
import { conversationSuggestionsSchema } from "@/lib/suggestions/conversationSuggestionContract";
import { actionReceiptWireSchema } from "@/types/actionRequest.schema";
import { workflowSnapshotWireSchema } from "@/types/workflowSnapshot.schema";

export { workflowSnapshotWireSchema } from "@/types/workflowSnapshot.schema";

const streamVersionSchema = z.literal("v1");
const nonBlankEnvelopeIdSchema = z
  .string()
  .refine((value) => value.trim().length > 0, "Stream envelope id must not be blank");

const partStartPayloadSchema = z
  .object({
    partId: z.string(),
    partType: z.enum(["reasoning", "text", "tool", "data"]),
    toolCallId: z.string().nullish(),
    kind: z.string().nullish(),
  })
  .strict()
  .superRefine((value, context) => {
    if (
      value.partType === "data" &&
      (typeof value.kind !== "string" || value.kind.trim().length === 0)
    ) {
      context.addIssue({
        code: "custom",
        path: ["kind"],
        message: "Data parts require a nonblank kind",
      });
    }
  });

const partDeltaPayloadSchema = z.object({
  partId: z.string(),
  text: z.string().nullish(),
  patch: z.record(z.string(), z.unknown()).nullish(),
  toolPayload: z.unknown().nullish(),
  dataPayload: z.unknown().nullish(),
});

const partEndPayloadSchema = z.object({
  partId: z.string(),
});

const transitionStartPayloadSchema = z.object({
  fromAgent: z.string(),
  toAgent: z.string(),
  summary: z.string().nullish(),
});

const transitionDeltaPayloadSchema = z.object({
  text: z.string(),
});

const transitionEndPayloadSchema = z.object({
  fromAgent: z.string(),
  toAgent: z.string(),
});

const messageCompletePayloadSchema = z.object({
  snapshotVersion: z.number().nullish(),
  snapshot: workflowSnapshotWireSchema.optional(),
});

const workflowSnapshotUpdatedPayloadSchema = z.object({
  snapshot: workflowSnapshotWireSchema,
});

const suggestionsReplacePayloadSchema = conversationSuggestionsSchema;

const streamErrorPayloadSchema = z.object({
  detail: z.string().nullish(),
  title: z.string().nullish(),
  code: z.string().nullish(),
  status: z.number().nullish(),
});

const routingDecidedPayloadSchema = z
  .object({
    selectedAgent: z.string(),
  })
  .strict();

const toolCallStartedPayloadSchema = z.object({
  toolCallId: z.string(),
  toolName: z.string(),
  args: z.unknown().nullish(),
  presentation: cnsToolExecutionArtifactSchema.nullish(),
});

const toolCallCompletedPayloadSchema = z.object({
  toolCallId: z.string(),
  toolName: z.string(),
  result: z.unknown().nullish(),
  error: z.boolean().nullish(),
  presentation: cnsToolExecutionArtifactSchema.nullish(),
});

const actionRequestCompletedPayloadSchema = z
  .object({
    actionRequestId: nonBlankEnvelopeIdSchema,
    assistantMessagePublicId: nonBlankEnvelopeIdSchema,
    streamPartId: nonBlankEnvelopeIdSchema,
    receipt: actionReceiptWireSchema,
  })
  .strict();

const artifactPatchPayloadSchema = z.object({
  artifactId: z.string(),
  op: z.string(),
  path: z.string(),
  value: z.unknown().nullish(),
  snapshotVersion: z.number().nullish(),
});

const activityStatusSchema = z.enum(["running", "done", "error"]);

const activityStartedPayloadSchema = z.object({
  activityId: z.string(),
  kind: z.string(),
  label: z.string(),
  detail: z.string().nullish(),
});

const activityUpdatedPayloadSchema = z.object({
  activityId: z.string(),
  status: activityStatusSchema.nullish(),
  label: z.string().nullish(),
  detail: z.string().nullish(),
});

const activityCompletedPayloadSchema = z.object({
  activityId: z.string(),
  status: activityStatusSchema,
  detail: z.string().nullish(),
});

export const projectCitedSourceSchema = z.object({
  sourceType: z.enum(SOURCE_TYPES),
  publicId: z.string(),
  name: z.string(),
  pinned: z.boolean(),
  chunkCount: z.number(),
});

export const mcpResultCitedSourceSchema = z.object({
  sourceType: z.literal("mcpResult"),
  providerKey: z.string().min(1),
  resourceId: z.string().min(1),
  name: z.string().min(1),
  resourceKind: z.string().min(1).optional(),
  url: z
    .string()
    .url()
    .refine((url) => url.startsWith("https://"), "Live source URLs must use HTTPS")
    .optional(),
});

export const citedSourceSchema = z.discriminatedUnion("sourceType", [
  projectCitedSourceSchema,
  mcpResultCitedSourceSchema,
]);

export const sourcesUsedPayloadSchema = z.object({
  sources: z.array(citedSourceSchema),
});

export type ProjectCitedSource = z.infer<typeof projectCitedSourceSchema>;
export type McpResultCitedSource = z.infer<typeof mcpResultCitedSourceSchema>;
export type CitedSource = z.infer<typeof citedSourceSchema>;

const envelopeBaseSchema = z.object({
  version: streamVersionSchema,
  seq: z.number().int().positive(),
  conversationId: nonBlankEnvelopeIdSchema,
  messageId: nonBlankEnvelopeIdSchema,
  timestamp: z.string(),
});

export const conversationStreamEnvelopeSchema = z.discriminatedUnion("eventType", [
  envelopeBaseSchema.extend({
    eventType: z.literal("part.start"),
    payload: partStartPayloadSchema,
  }),
  envelopeBaseSchema.extend({
    eventType: z.literal("part.delta"),
    payload: partDeltaPayloadSchema,
  }),
  envelopeBaseSchema.extend({
    eventType: z.literal("part.end"),
    payload: partEndPayloadSchema,
  }),
  envelopeBaseSchema.extend({
    eventType: z.literal("transition.start"),
    payload: transitionStartPayloadSchema,
  }),
  envelopeBaseSchema.extend({
    eventType: z.literal("transition.delta"),
    payload: transitionDeltaPayloadSchema,
  }),
  envelopeBaseSchema.extend({
    eventType: z.literal("transition.end"),
    payload: transitionEndPayloadSchema,
  }),
  envelopeBaseSchema.extend({
    eventType: z.literal("message.complete"),
    payload: messageCompletePayloadSchema,
  }),
  envelopeBaseSchema.extend({
    eventType: z.literal("error"),
    payload: streamErrorPayloadSchema,
  }),
  envelopeBaseSchema.extend({
    eventType: z.literal("routing.decided"),
    payload: routingDecidedPayloadSchema,
  }),
  envelopeBaseSchema.extend({
    eventType: z.literal("tool_call.started"),
    payload: toolCallStartedPayloadSchema,
  }),
  envelopeBaseSchema.extend({
    eventType: z.literal("tool_call.completed"),
    payload: toolCallCompletedPayloadSchema,
  }),
  envelopeBaseSchema.extend({
    eventType: z.literal("action_request.completed"),
    payload: actionRequestCompletedPayloadSchema,
  }),
  envelopeBaseSchema.extend({
    eventType: z.literal("artifact.patch"),
    payload: artifactPatchPayloadSchema,
  }),
  envelopeBaseSchema.extend({
    eventType: z.literal("activity.started"),
    payload: activityStartedPayloadSchema,
  }),
  envelopeBaseSchema.extend({
    eventType: z.literal("activity.updated"),
    payload: activityUpdatedPayloadSchema,
  }),
  envelopeBaseSchema.extend({
    eventType: z.literal("activity.completed"),
    payload: activityCompletedPayloadSchema,
  }),
  envelopeBaseSchema.extend({
    eventType: z.literal("sources.used"),
    payload: sourcesUsedPayloadSchema,
  }),
  envelopeBaseSchema.extend({
    eventType: z.literal("workflow.snapshot.updated"),
    payload: workflowSnapshotUpdatedPayloadSchema,
  }),
  envelopeBaseSchema.extend({
    eventType: z.literal("suggestions.replace"),
    payload: suggestionsReplacePayloadSchema,
  }),
]);

export type ConversationStreamEnvelope = z.infer<typeof conversationStreamEnvelopeSchema>;

export function parseConversationStreamEnvelope(input: unknown): ConversationStreamEnvelope | null {
  const result = conversationStreamEnvelopeSchema.safeParse(input);
  return result.success ? result.data : null;
}
